/* The quote email: the message the user wrote, the quote itself as a table, a pay-online button (the draft order's
 * invoice link, which opens Shopify's own checkout), and the signature. Rendered here so the composer previews
 * exactly what leaves; the Worker only hands it to Gmail. Plain text alternative included. */
import { money, lineTotal, quoteNumber } from './quote.js';
import { displayTitle, displayDetail } from './paint.js';

export const DEFAULT_SETTINGS = {
  fromName: 'AutofxMart',
  subject: 'Your quote {number} from AutofxMart',
  greeting: 'Hi {first},',
  intro: 'Thanks for reaching out. Here is your quote — everything is mixed to order for your exact color code.',
  closing: 'You can pay securely online with the button below, or just reply to this email with any questions.',
  signature: 'AutofxMart\nsupport@autofxmart.com\nautofxmart.com',
  validDays: 30,
  shipStandard: 14.99,
  shipFreeOver: 97.99,
  activatorRatio: 4,
  activatorProduct: 'act',
  reducerRatio: 1,
  followUp: 'Hi {first},\n\nJust following up on quote {number}. Let me know if you have any questions or want to change anything.\n\n',
};

export const settingsWith = (s) => ({ ...DEFAULT_SETTINGS, ...(s || {}) });

export function fill(template, vars) {
  return String(template || '').replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m));
}

export function vars(q) {
  const first = q.customer?.firstName || (q.customer?.displayName || '').split(' ')[0] || 'there';
  return { first, name: q.customer?.displayName || '', number: quoteNumber(q), total: money(q.totals?.total ?? 0) };
}

/* The editable message the composer starts with. */
export function defaultMessage(q, settings) {
  const s = settingsWith(settings);
  const v = vars(q);
  return [fill(s.greeting, v), '', fill(s.intro, v), '', fill(s.closing, v)].join('\n');
}

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const paras = (text) =>
  String(text || '')
    .split(/\n{2,}/)
    .map((p) => `<p style="margin:0 0 14px;line-height:1.55">${esc(p).replace(/\n/g, '<br>')}</p>`)
    .join('');

export const lineDetail = displayDetail;

function validUntil(q, days, now = new Date()) {
  if (!days) return '';
  const base = q.sentAt ? new Date(q.sentAt) : now;
  const d = new Date(base.getTime() + days * 86400000);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/* One quote's table, totals and pay button. `label` heads it ("Quote D39", or "Option B — Gallon kit"). */
function quoteBlock(q, { label, payButton, payLabel }) {
  const t = q.totals || {};
  const pay = payButton && q.invoiceUrl;
  const rows = q.lines
    .map((l) => {
      const swatch = l.kind === 'paint' && l.hex
        ? `<span style="display:inline-block;width:14px;height:14px;border-radius:3px;background:${esc(l.hex)};border:1px solid #0002;vertical-align:-2px;margin-right:6px"></span>`
        : '';
      return `<tr>
        <td style="padding:10px 8px;border-bottom:1px solid #e5e5e5;vertical-align:top">
          <div style="font-weight:600">${swatch}${esc(displayTitle(l))}</div>
          <div style="color:#666;font-size:13px;margin-top:2px">${esc(lineDetail(l))}</div>
        </td>
        <td style="padding:10px 8px;border-bottom:1px solid #e5e5e5;text-align:center;vertical-align:top;white-space:nowrap">${l.quantity}</td>
        <td style="padding:10px 8px;border-bottom:1px solid #e5e5e5;text-align:right;vertical-align:top;white-space:nowrap">${money(l.unitPrice)}</td>
        <td style="padding:10px 8px;border-bottom:1px solid #e5e5e5;text-align:right;vertical-align:top;white-space:nowrap">${money(lineTotal(l))}</td>
      </tr>`;
    })
    .join('');

  const sumRow = (lbl, value, strong) =>
    `<tr><td colspan="3" style="padding:4px 8px;text-align:right;${strong ? 'font-weight:700;font-size:16px' : 'color:#555'}">${esc(lbl)}</td>
      <td style="padding:4px 8px;text-align:right;white-space:nowrap;${strong ? 'font-weight:700;font-size:16px' : ''}">${value}</td></tr>`;

  const sums = [
    sumRow('Subtotal', money(t.subtotal)),
    t.discount ? sumRow(q.discount?.title || 'Discount', `−${money(t.discount)}`) : '',
    q.shipping ? sumRow(q.shipping.title || 'Shipping', money(t.shipping)) : '',
    t.tax && !t.taxesIncluded ? sumRow('Tax', money(t.tax)) : '',
    sumRow('Total', money(t.total), true),
    t.tax && t.taxesIncluded ? sumRow(`Includes ${money(t.tax)} sales tax`, '') : '',
  ].join('');

  const html = `
    <div style="margin:22px 0 6px;font-size:13px;letter-spacing:.06em;text-transform:uppercase;color:#666">${esc(label)}</div>
    <table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;font-size:14px">
      <tr style="color:#666;font-size:12px;text-transform:uppercase;letter-spacing:.04em">
        <th style="text-align:left;padding:6px 8px;border-bottom:2px solid #1a1a1a">Item</th>
        <th style="text-align:center;padding:6px 8px;border-bottom:2px solid #1a1a1a">Qty</th>
        <th style="text-align:right;padding:6px 8px;border-bottom:2px solid #1a1a1a">Price</th>
        <th style="text-align:right;padding:6px 8px;border-bottom:2px solid #1a1a1a">Total</th>
      </tr>
      ${rows}
      ${sums}
    </table>
    ${pay ? `<div style="text-align:center;margin:22px 0 8px">
      <a href="${esc(q.invoiceUrl)}" style="display:inline-block;background:#1a1a1a;color:#fff;text-decoration:none;padding:13px 28px;border-radius:8px;font-weight:600">${esc(payLabel)}</a>
    </div>` : ''}`;

  const text = [
    label.toUpperCase(),
    q.lines.map((l) => `- ${displayTitle(l)}${lineDetail(l) ? ` (${lineDetail(l)})` : ''}\n  ${l.quantity} × ${money(l.unitPrice)} = ${money(lineTotal(l))}`).join('\n'),
    '',
    `Subtotal: ${money(t.subtotal)}`,
    t.discount ? `${q.discount?.title || 'Discount'}: -${money(t.discount)}` : '',
    q.shipping ? `${q.shipping.title || 'Shipping'}: ${money(t.shipping)}` : '',
    t.tax && !t.taxesIncluded ? `Tax: ${money(t.tax)}` : '',
    `Total: ${money(t.total)}${t.tax && t.taxesIncluded ? ` (includes ${money(t.tax)} sales tax)` : ''}`,
    pay ? `${payLabel}: ${q.invoiceUrl}` : '',
  ].filter((x) => x !== '').join('\n');
  return { html, text };
}

export const optionLabel = (q, i) => {
  const letter = String.fromCharCode(65 + i);
  const name = (q.option || '').trim();
  return /^option\s+[a-z]$/i.test(name) || !name ? `Option ${letter}` : `Option ${letter} — ${name}`;
};

/* {subject, html, text} for a quote, or for several options of one quote sent together (each with its own pay
   button). `message` is what the user wrote. */
export function renderQuoteEmail(qOrOptions, { message, subject, settings, payButton = true, shop = {} }) {
  const s = settingsWith(settings);
  const list = Array.isArray(qOrOptions) ? qOrOptions : [qOrOptions];
  const q = list[0];
  const many = list.length > 1;
  const until = validUntil(q, Number(s.validDays) || 0);
  const blocks = list.map((x, i) =>
    quoteBlock(x, {
      label: many ? optionLabel(x, i) : `Quote ${quoteNumber(x)}`,
      payButton,
      payLabel: many ? `Choose option ${String.fromCharCode(65 + i)} & pay online` : 'Review & pay online',
    }),
  );

  const html = `<!doctype html><html><body style="margin:0;padding:0;background:#f4f4f4">
<div style="max-width:640px;margin:0 auto;padding:24px 12px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;color:#1a1a1a">
  <div style="background:#fff;border-radius:10px;padding:28px 24px;border:1px solid #e5e5e5">
    ${paras(message)}
    ${blocks.map((b) => b.html).join('<div style="height:18px"></div>')}
    ${until ? `<p style="color:#666;font-size:13px;text-align:center;margin:10px 0 0">Prices held until ${esc(until)}.</p>` : ''}
    <div style="margin-top:26px;color:#333">${esc(s.signature).replace(/\n/g, '<br>')}</div>
  </div>
  ${shop.address ? `<p style="color:#999;font-size:12px;text-align:center;margin:14px 0 0">${esc(shop.address)}</p>` : ''}
</div></body></html>`;

  const text = [String(message || '').trim(), '', blocks.map((b) => b.text).join('\n\n'), until ? `\nPrices held until ${until}.` : '', '', s.signature]
    .filter((x) => x !== '')
    .join('\n');

  return { subject: subject || fill(s.subject, vars(q)), html, text };
}

/* A plain email (the Email page, follow-ups): the message and the signature, nothing else. */
export function renderPlainEmail({ message, settings }) {
  const s = settingsWith(settings);
  const html = `<!doctype html><html><body style="margin:0;padding:0">
<div style="max-width:640px;padding:8px 4px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;color:#1a1a1a">
${paras(message)}<div style="margin-top:18px;color:#333">${esc(s.signature).replace(/\n/g, '<br>')}</div></div></body></html>`;
  const text = `${String(message || '').trim()}\n\n${s.signature}`;
  return { html, text };
}
